import { useQuery } from '@tanstack/react-query'

import { fetchDivisions, fetchSummary } from '@/features/portfolio/api'
import type { ApiError } from '@/lib/api/client'
import type { Division, PortfolioSummary } from '@/types/domain'

const EMPTY: PortfolioSummary = {
  divisions: 0,
  projects: 0,
  clients: 0,
  people: null,
  byStage: {},
}

/**
 * The portfolio's headline figures.
 *
 * Counts come from the summary endpoint rather than from the project list,
 * because that list is paginated: a page of 50 would report a 70-project
 * portfolio as 50.
 *
 * There is no bundled fallback any more. There used to be — the whole AWJ
 * portfolio, 70 projects and 41 client names, compiled into the JavaScript
 * bundle so a demo would still render with the API down. That file was
 * downloadable by anyone who could reach the login page, signed in or not.
 * A screen that says "cannot reach the API" is a better product than one that
 * quietly serves the client list to the internet.
 */
export function usePortfolio() {
  const divisions = useQuery<Division[], ApiError>({
    queryKey: ['divisions'],
    queryFn: fetchDivisions,
    retry: false,
  })

  const summary = useQuery<PortfolioSummary, ApiError>({
    queryKey: ['portfolio-summary'],
    queryFn: fetchSummary,
    retry: false,
  })

  return {
    isLive: divisions.isSuccess && summary.isSuccess,
    isPending: divisions.isPending || summary.isPending,
    isError: divisions.isError || summary.isError,
    error: divisions.error ?? summary.error,
    divisions: divisions.data ?? [],
    summary: summary.data ?? EMPTY,
  }
}
